import React from 'react';
import type { TeamRepoScore } from '@sdd-maturity/shared-types';
import { LEVEL_BAR_COLORS, LEVEL_ORDER, levelLabel } from '../lib/score';
import { classifyRepos } from '../lib/utils';

export interface RepoMaturityDistributionProps {
  repos: TeamRepoScore[];
}

/**
 * Full-width maturity-distribution bar for a team's repos. Split out of
 * TeamScoreHeader so it can sit below the repo table while the stat cards stay on
 * top. Reflects only the active repos — the same set shown in the "Active repos"
 * table — so it never counts levels carried by stale/skipped/failed repos.
 */
export function RepoMaturityDistribution({ repos }: RepoMaturityDistributionProps): React.JSX.Element {
  const activeRepos = classifyRepos(repos).active;

  const levelCounts: Record<string, number> = {};
  for (const r of activeRepos) {
    levelCounts[r.overallLevel] = (levelCounts[r.overallLevel] ?? 0) + 1;
  }

  const levels = LEVEL_ORDER.filter((l) => levelCounts[l]);
  const leveledCount = levels.reduce((sum, l) => sum + (levelCounts[l] ?? 0), 0);

  return (
    <div data-testid="maturity-distribution" className="card p-md mb-md">
      {levels.length > 0 ? (
        <>
          {/* Stacked bar */}
          <div className="distribution-bar mb-sm">
            {levels.map((level) => {
              const count = levelCounts[level] ?? 0;
              const pct = leveledCount > 0 ? (count / leveledCount) * 100 : 0;
              return (
                <div
                  key={level}
                  className="distribution-bar-segment"
                  title={`${levelLabel(level)}: ${count} repo${count !== 1 ? 's' : ''} (${Math.round(pct)}%)`}
                  style={{ width: `${pct}%`, backgroundColor: LEVEL_BAR_COLORS[level] }}
                />
              );
            })}
          </div>

          {/* Legend */}
          <div className="distribution-legend">
            {levels.map((level) => (
              <span key={level} className="distribution-legend-item">
                <span className="distribution-legend-swatch" style={{ backgroundColor: LEVEL_BAR_COLORS[level] }} />
                <span className="text-muted">{levelLabel(level)}</span>
                <strong style={{ color: LEVEL_BAR_COLORS[level] }}>{levelCounts[level]}</strong>
              </span>
            ))}
          </div>
        </>
      ) : (
        <span className="text-base text-muted text-center" style={{ display: 'block' }}>No scan data</span>
      )}
      <span className="text-base text-muted text-center mt-xs" style={{ display: 'block' }}>Maturity Distribution</span>
    </div>
  );
}
